import { css } from '@emotion/react';
import { parse as himalaya } from 'himalaya';

import { Element, Node, NodeType } from '../../types';
import { Node as HimalayaNode } from '../himalaya/types';
import htmlAttributes from './attributes/html.json';
import nonStandardAttributes from './attributes/non-standard.json';
import svgAttributes from './attributes/svg.json';
import decodeClient from './utils/decode/client';
import decodeServer from './utils/decode/server';

export const decode = typeof window !== 'undefined' ? decodeClient : decodeServer;

// Map of lowercase HTML and SVG attributes to get their camelCase version.
const attributesMap: Record<string, string> = htmlAttributes
  .concat(nonStandardAttributes)
  .concat(svgAttributes)
  .reduce(function (soFar: Record<string, string>, attr) {
    const lower = attr.toLowerCase();
    if (lower !== attr) {
      soFar[lower] = attr;
    }
    return soFar;
  }, {});

/**
 * Adapts the Himalaya AST Specification v1 to our format.
 *
 * @param himalayaNode - The node coming from himalaya.
 * @param parent - The parent node.
 *
 * @returns The final and modified node.
 */
const adaptNode = (himalayaNode: HimalayaNode, parent?: Element): Node[] | null => {
  if (himalayaNode.type === NodeType.Element) {
    const node: Node = {
      type: himalayaNode.type,
      component: himalayaNode.tagName,
      props: himalayaNode.attributes.reduce((props: Element['props'], { key, value }) => {
        // WordPress returns links with HTML escaped entities, so we have to decode them
        if (typeof value === 'string') value = decode(value);

        // mapping from HTML attribute names to react names:  // https://github.com/facebook/react/blob/58b8797b7372c9296e65e08ce8297e4a394b7972/packages/react-dom/src/shared/DOMProperty.js#L241-L244
        if (key === 'class') {
          props.className = value;
        } else if (key === 'for') {
          props.htmlFor = value;
        } else if (/^data-/.test(key)) {
          Object.assign(props, {
            [key]: value
          });
          // props[key] = value;
        } else if (key === 'style') {
          // Add inline styles to the component with `emotion`.
          props.css = css(value ?? '');
        } else if (!/^on/.test(key) && key !== 'className') {
          const camelCaseKey = attributesMap[key.replace(/[-:]/, '').toLowerCase()];
          // Map keys with no value to `true` booleans.
          Object.assign(props, {
            [camelCaseKey || key]: value === null ? true : value
          });
        }

        return props;
      }, {}),
      parent
    };

    if (node.props?.['data-hidden'] === 'true') {
      return null;
    }

    node.children = himalayaNode.children.reduce((tree: Node[], child): Node[] => {
      const childAdapted = adaptNode(child, node as Element);
      if (childAdapted) tree.push(...childAdapted);
      return tree;
    }, []);

    return [node];
  }

  if (himalayaNode.type === NodeType.Text) {
    const content = decode(himalayaNode.content);

    if (content.trim().length) {
      // return [
      //   {
      //     type: himalayaNode.type,
      //     content: content,
      //     parent,
      //   },
      // ];

      const parts = content.split(/(\{\{.*?}})/g);
      return parts.map(part => {
        if (/\{\{.*?}}/.test(part)) {
          // This is a variable
          return {
            type: NodeType.Variable,
            props: {
              variable: part.slice(2, -2) // Remove {{ and }}
            },
            parent
          };
        } else {
          // This is a text
          return {
            type: himalayaNode.type,
            content: part,
            parent
          };
        }
      });
    } else return null;
  }

  if (himalayaNode.type === NodeType.Comment) {
    const content = decode(himalayaNode.content);

    if (content.trim().length) {
      return [
        {
          type: himalayaNode.type,
          content: content,
          parent
        }
      ];
    } else return null;
  }

  return null;
};

/**
 * Parses the HTML and returns AST.
 *
 * @param html - The HTML from content.
 *
 * @returns The AST of the HTML.
 */
const parse = (html: string): Node[] =>
  himalaya(html).reduce((tree: Node[], element) => {
    const adapted = adaptNode(element);
    if (adapted) tree.push(...adapted);
    return tree;
  }, []);

export default parse;
